import React from 'react';
import {
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  List,
  Box,
  Divider,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  BusinessRounded,
  FolderRounded,
  FactCheckRounded,
  HomeRounded,
  LibraryBooksRounded,
  LogoutRounded,
  SchoolRounded,
} from '@mui/icons-material';
import { Prepa5LogoBlack } from 'assets';
import {
  StyledDrawer,
  LogoContainer,
  NavigationContainer,
  UserSection,
  UserAvatar,
  UserInfo,
  LogoutButton,
} from './styles';
import {
  ROOT,
  LOGIN,
  DEPARTAMENTOS,
  MIS_RECURSOS,
  MIS_MATERIAS,
  RECURSOS,
  AUDITORIA,
} from '../../navigation/constants';
import { apiUrl } from '../../config/api';
import { RootState } from '../../state-management/store';
import { clearUser } from '../../state-management/slices/Global';
import { EUserRole } from '../../types/user';

interface NavigationItem {
  label: string;
  path: string;
  icon: React.ReactNode;
}

interface NavigationSection {
  title?: string;
  items: NavigationItem[];
}

const NavigationDrawer: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.global.user);

  const getInitials = (firstName: string, lastName: string) =>
    `${firstName[0]}${lastName[0]}`.toUpperCase();

  const handleLogout = async () => {
    try {
      await fetch(apiUrl('/api/auth/logout'), { method: 'POST', credentials: 'include' });
    } catch {
      /* still clear local session */
    }
    dispatch(clearUser());
    navigate(LOGIN);
  };

  const buildNavigationSections = (): NavigationSection[] => {
    const role = user?.role;
    const sections: NavigationSection[] = [];

    const menuItems: NavigationItem[] = [{ label: 'Inicio', path: ROOT, icon: <HomeRounded /> }];

    if (role === EUserRole.ALUMNO) {
      menuItems.push({ label: 'Mis materias', path: MIS_MATERIAS, icon: <SchoolRounded /> });
    }

    if (role === EUserRole.DOCENTE) {
      menuItems.push({ label: 'Mis recursos', path: MIS_RECURSOS, icon: <FolderRounded /> });
    }

    if (role === EUserRole.AUDITOR || role === EUserRole.ADMINISTRADOR) {
      menuItems.push({ label: 'Revisión de Recursos', path: RECURSOS, icon: <FactCheckRounded /> });
    }

    sections.push({ title: 'MENÚ', items: menuItems });

    if (role === EUserRole.ADMINISTRADOR) {
      sections.push({
        title: 'ADMINISTRACIÓN',
        items: [
          { label: 'Departamentos', path: DEPARTAMENTOS, icon: <BusinessRounded /> },
          { label: 'Auditoría', path: AUDITORIA, icon: <LibraryBooksRounded /> },
        ],
      });
    }

    return sections;
  };

  const navigationSections = buildNavigationSections();

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  return (
    <StyledDrawer variant="permanent">
      {/* Logo and School Name Section */}
      <LogoContainer>
        <img src={Prepa5LogoBlack} alt="Prepa5 Logo" width={72} height={72} />
        <Typography variant="h3" color="primary.main">
          Escuela Preparatoria 5
        </Typography>
      </LogoContainer>

      {/* Navigation Sections */}
      <NavigationContainer>
        {navigationSections.map((section, sectionIndex) => (
          <Box key={sectionIndex}>
            {section.title && <Typography variant="caption">{section.title}</Typography>}
            <List>
              {section.items.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <ListItem key={item.path} disablePadding>
                    <ListItemButton selected={isActive} onClick={() => handleNavigation(item.path)}>
                      <ListItemIcon>{item.icon}</ListItemIcon>
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          fontWeight: isActive ? 600 : 400,
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        ))}
      </NavigationContainer>

      {/* User & Logout Section */}
      <Box>
        {user && (
          <UserSection>
            <UserAvatar src={user.avatarUrl ?? undefined}>
              {getInitials(user.firstName, user.lastName)}
            </UserAvatar>
            <UserInfo>
              <Typography variant="body2" fontWeight={600} noWrap>
                {user.firstName} {user.lastName}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {user.email}
              </Typography>
            </UserInfo>
          </UserSection>
        )}
        <Divider sx={{ my: 1 }} />
        <List disablePadding>
          <ListItem disablePadding>
            <LogoutButton onClick={handleLogout}>
              <ListItemIcon>
                <LogoutRounded fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Cerrar sesión" primaryTypographyProps={{ fontWeight: 500 }} />
            </LogoutButton>
          </ListItem>
        </List>
      </Box>
    </StyledDrawer>
  );
};

export default NavigationDrawer;
